import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { Menu, Scan, X } from "lucide-react";
import { isLocaleHomePath, localePath } from "@/config/site";
import { useI18n } from "@/i18n/I18nContext";
import { LanguageSwitcher } from "@/i18n/LanguageSwitcher";
import { GooglePlayBadgeLink } from "@/landing/components/GooglePlayBadgeLink";
import { GooglePlayQrCode } from "@/landing/components/GooglePlayQrCode";

export function Header() {
  const { messages, locale } = useI18n();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const homePath = localePath(locale);
  const isHome = isLocaleHomePath(location.pathname, locale);

  const navItems = [
    { label: messages.header.navFeatures, sectionId: "features" },
    { label: messages.header.navHowItWorks, sectionId: "how-it-works" },
    { label: messages.header.navPrivacy, sectionId: "privacy" },
    { label: messages.header.navUseCases, sectionId: "use-cases" },
  ] as const;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const goToSection = (
    event: React.MouseEvent<HTMLAnchorElement>,
    sectionId: string,
  ) => {
    if (!isHome) return;
    event.preventDefault();
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  return (
    <motion.header
      className="fixed top-0 right-0 left-0 z-50 border-b border-gray-200/50 bg-white/80 backdrop-blur-md"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        className="container mx-auto px-6 transition-all duration-300 lg:px-12"
        animate={{
          paddingTop: isScrolled ? "0.75rem" : "1.5rem",
          paddingBottom: isScrolled ? "0.75rem" : "1.5rem",
        }}
      >
        <div className="flex items-center justify-between gap-3">
          <motion.div
            animate={{
              scale: isScrolled ? 0.9 : 1,
            }}
            transition={{ duration: 0.3 }}
            className="min-w-0"
          >
            <Link
              to={homePath}
              className="flex min-w-0 items-center gap-3 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-[#4CAF50]/40"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#4CAF50]">
                <Scan className="h-6 w-6 text-white" />
              </div>
              <span
                className="truncate text-2xl text-[#111111]"
                style={{ fontFamily: "Inter, system-ui, sans-serif" }}
              >
                WasOK
              </span>
            </Link>
          </motion.div>

          <div className="hidden flex-1 items-center justify-end gap-6 md:flex">
            <nav className="flex items-center gap-8">
              {navItems.map((item) => (
                <motion.div
                  key={item.sectionId}
                  whileHover={{ y: -2 }}
                  transition={{ duration: 0.2 }}
                >
                  <Link
                    to={`${homePath}#${item.sectionId}`}
                    onClick={(e) => goToSection(e, item.sectionId)}
                    className="cursor-pointer text-gray-600 transition-colors duration-200 hover:text-[#4CAF50]"
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <LanguageSwitcher />
            <div className="flex shrink-0 items-center gap-2">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex shrink-0"
              >
                <GooglePlayBadgeLink
                  locale={locale}
                  ariaLabel={messages.hero.googlePlay}
                  className="inline-block rounded-md outline-none focus-visible:ring-2 focus-visible:ring-[#4CAF50]/40"
                  imgClassName="h-9 w-auto md:h-10"
                />
              </motion.div>
              <GooglePlayQrCode ariaLabel={messages.cta.scanQr} />
            </div>
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <LanguageSwitcher className="max-w-[7.5rem] truncate rounded-lg border border-gray-200 bg-white px-1.5 py-1.5 text-xs text-gray-800 shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-[#4CAF50]/30" />
            <button
              type="button"
              className="shrink-0 p-2"
              onClick={() => setMobileMenuOpen((open) => !open)}
              aria-expanded={mobileMenuOpen}
              aria-label={
                mobileMenuOpen
                  ? messages.header.closeMenu
                  : messages.header.openMenu
              }
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6 text-[#111111]" />
              ) : (
                <Menu className="h-6 w-6 text-[#111111]" />
              )}
            </button>
          </div>
        </div>

        <AnimatePresence initial={false}>
          {mobileMenuOpen ? (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden md:hidden"
            >
              <nav className="flex flex-col gap-4 pt-4 pb-2">
                {navItems.map((item) => (
                  <Link
                    key={item.sectionId}
                    to={`${homePath}#${item.sectionId}`}
                    onClick={(e) => goToSection(e, item.sectionId)}
                    className="cursor-pointer py-2 text-gray-600 transition-colors duration-200 hover:text-[#4CAF50]"
                  >
                    {item.label}
                  </Link>
                ))}
                <div className="flex items-center justify-center gap-3 pt-1">
                  <GooglePlayBadgeLink
                    locale={locale}
                    ariaLabel={messages.hero.googlePlay}
                    className="inline-block rounded-md outline-none focus-visible:ring-2 focus-visible:ring-[#4CAF50]/40"
                    imgClassName="h-10 w-auto max-w-full"
                  />
                  <GooglePlayQrCode
                    ariaLabel={messages.cta.scanQr}
                    containerClassName="h-10 w-10 shrink-0 rounded-md bg-white p-0.5"
                  />
                </div>
              </nav>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </motion.div>
    </motion.header>
  );
}
